'use client'

import HeaderGap from '@/components/atoms/headerGap'
import Container from '@/components/templates/container'
import Layout from '@/components/templates/layout'
import titleCase from '@/utils/titleCase'
import { Pricing, body, h2 } from '@/components/organisms/sportPage'
import GymPricing from '@/components/organisms/sportPage/gymPricing'

const MembershipTiers = ({ membership }) => {
  if (!membership?.tiers?.length) return null
  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      <section id="membership" className="scroll-mt-[100px]">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 lg:gap-20 mb-10 lg:mb-12">
          <h2 className={h2}>{titleCase(membership.title || 'Membership')}</h2>
          {membership.description && <p className={`${body} max-w-[560px]`}>{membership.description}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          {membership.tiers.map((tier, i) => (
            <div key={i} className="flex flex-col gap-3 p-7 lg:p-9 border border-dune">
              <span className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">{titleCase(tier.name)}</span>
              {tier.price && <span className="font-ttDrugsTrial text-olive text-[30px] lg:text-[36px] leading-none">{tier.price}</span>}
              {tier.features?.length > 0 && (
                <ul className="mt-3 border-b border-dune">
                  {tier.features.map((f, j) => (
                    <li key={j} className="font-chivoMono text-[13px] text-umber border-t border-dune py-2.5">{f}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        {membership.note && <p className="font-chivoMono text-[13px] text-khaki mt-4">{membership.note}</p>}
      </section>
    </Container>
  )
}

// Scroll to a section smoothly (instant for people who prefer reduced motion)
const smoothScroll = (e, href) => {
  const target = document.querySelector(href)
  if (!target) return
  e.preventDefault()
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  history.replaceState(null, '', href)
}

const PricingPage = ({ pricingAPI, residentPlans, footerAPI, settingAPI }) => {
  const p = pricingAPI || {}
  const jump = [
    p.padel && ['#padel', 'Padel'],
    p.tennis && ['#tennis', 'Tennis'],
    p.gym && ['#gym', 'Gym'],
    p.membership?.tiers?.length && ['#membership', 'Membership'],
  ].filter(Boolean)

  return (
    <Layout settingAPI={settingAPI} footerAPI={footerAPI}>
      <HeaderGap />

      {/* Header: centred title, intro and section links */}
      <Container outerClassName="mt-[60px] lg:mt-[100px]">
        <div className="flex flex-col items-center text-center gap-6">
          <span className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">Prices</span>
          <h1 className="font-ttDrugsTrial text-olive text-[40px] md:text-[56px] leading-none max-w-[900px]">
            {p.pageTitle?.trim() ? titleCase(p.pageTitle) : 'Padel, Tennis & Gym Prices in Sayan, Ubud'}
          </h1>
          {p.intro && <p className={`${body} max-w-[640px]`}>{p.intro}</p>}
          {jump.length > 1 && (
            <nav aria-label="Price sections" className="flex flex-wrap justify-center gap-2 mt-2">
              {jump.map(([href, label]) => (
                <a key={href} href={href} onClick={(e) => smoothScroll(e, href)}
                  className="min-h-[44px] inline-flex items-center px-5 border border-khaki/40 font-chivoMono text-[12px] uppercase tracking-widest text-umber hover:border-forest hover:text-olive transition-colors duration-200">
                  {label}
                </a>
              ))}
            </nav>
          )}
        </div>
      </Container>

      {p.padel && <Pricing data={p.padel} fallbackTitle="Padel courts & lessons" id="padel" />}
      {p.tennis && <Pricing data={p.tennis} fallbackTitle="Tennis courts & lessons" id="tennis" />}
      {p.gym && <GymPricing gym={p.gym} plans={residentPlans} id="gym" />}
      <MembershipTiers membership={p.membership} />

      {p.ctaNote && (
        <Container outerClassName="mt-[80px] lg:mt-[100px]">
          <p className="font-chivoMono text-[13px] text-khaki max-w-[640px]">{p.ctaNote}</p>
        </Container>
      )}

      <div className="pb-[100px] lg:pb-[150px]" />
    </Layout>
  )
}

export default PricingPage
